import { User } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { DashboardCard } from "./DashboardCard";
import { Calendar, Clock, Heart, Search, Loader2, ExternalLink } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from '@/lib/api';

interface GuestDashboardProps {
  user: User;
}

interface Booking {
  id: number;
  propertyId: number;
  propertyTitle: string;
  checkIn: string;
  checkOut: string;
  guestCount: number;
  totalAmount: number;
  status: string;
}

export function GuestDashboard({ user }: GuestDashboardProps) {
  // Fetch guest bookings data
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['guest/bookings'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'guest/bookings');
        // if (!response.ok) {
        //   throw new Error('Failed to fetch bookings');
        // }
        const data =  response;
        console.log('Bookings data:', data); // Debug log
        return data;
      } catch (error) {
        console.error('Error fetching bookings:', error);
        return { all: [], upcoming: [], past: [] };
      }
    }
  });

  // Fetch favorite properties if we have an endpoint for it
  const { data: favorites = [], isLoading: favoritesLoading } = useQuery({
    queryKey: ['favorites'],
    enabled: !!user,
    // If the endpoint doesn't exist, handle the error and return empty array
    queryFn: async () => {
      try {
        const res = await apiRequest('GET', 'favorites');
        // if (!res.ok) return [];
        // return res.json();
        return res;
      } catch (error) {
        console.error("Failed to fetch favorites:", error);
        return [];
      }
    }
  });

  // Functions to format data for display
  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateNights = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status: string) => {
    status = status.toLowerCase();
    switch (status) {
      case "confirmed":
        return <Badge className="bg-green-500">Confirmed</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case "cancelled":
        return <Badge className="bg-red-500">Cancelled</Badge>;
      case "completed":
        return <Badge className="bg-blue-500">Completed</Badge>;
      default:
        return <Badge>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
    }
  };

  // Show loading state while fetching data
  if (bookingsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
        <span className="text-muted-foreground">Loading your bookings...</span>
      </div>
    );
  }

  // Prepare dashboard data from API responses
  const today = new Date();
  const upcomingBookings = (bookingsData || []).filter((booking: Booking) => {
    const checkIn = new Date(booking.checkIn);
    const checkOut = new Date(booking.checkOut);
    return (
      (booking.status === "confirmed" || booking.status === "pending") && checkIn >= today
    );
  });
  const pastBookings = (bookingsData || []).filter((booking: Booking) => {
    const checkOut = new Date(booking.checkOut);
    return (
      booking.status === "completed" || booking.status === "cancelled" || checkOut < today
    );
  });
  const favoriteProperties = favorites.length;

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold">Welcome, {user.fullName ? user.fullName.split(' ')[0] : user.username}</h2>
        <p className="text-muted-foreground">Here's an overview of your stays and bookings</p>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <DashboardCard
          title="Upcoming Stays"
          value={upcomingBookings.length}
          icon={<Calendar className="h-6 w-6" />}
          subtitle="Bookings you have scheduled"
          iconBgClass="bg-blue-100"
          iconTextClass="text-blue-500"
        />
        <DashboardCard
          title="Past Bookings"
          value={pastBookings.length}
          icon={<Clock className="h-6 w-6" />}
          subtitle="Previous stays completed"
          iconBgClass="bg-purple-100"
          iconTextClass="text-purple-500"
        />
        <DashboardCard
          title="Saved Properties"
          value={favoriteProperties}
          icon={<Heart className="h-6 w-6" />}
          subtitle="Properties you saved"
          iconBgClass="bg-red-100"
          iconTextClass="text-red-500"
        />
      </div>

      {/* Action button */}
      <div className="my-8">
        <Button 
          size="lg" 
          className="flex items-center gap-2"
          asChild
        >
          <Link href="/properties">
            <Search className="h-5 w-5" />
            <span>Find a Stay</span>
          </Link>
        </Button>
      </div>

      {/* Upcoming bookings */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingBookings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Nights</TableHead>
                  <TableHead>Guests</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingBookings.map((booking: Booking) => (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {booking.propertyTitle || `Property #${booking.propertyId}`}
                        <a 
                          href={`/properties/${booking.propertyId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                          title="View property details"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{formatDate(booking.checkIn)}</div>
                      <div className="text-muted-foreground">to {formatDate(booking.checkOut)}</div>
                    </TableCell>
                    <TableCell>{calculateNights(booking.checkIn, booking.checkOut)}</TableCell>
                    <TableCell>{booking.guestCount}</TableCell>
                    <TableCell>{formatCurrency(booking.totalAmount)}</TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No upcoming bookings. Explore properties to find your next stay.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}